import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { STEW_TOOL_REGISTRY } from "@/lib/stewToolRegistry";
import type { RelatedAction } from "@/components/library/RelatedActions";

/**
 * Admin-only list editor for sop_documents.related_actions.
 * type + key are machinery; label is display copy shown to members
 * (docs render by their live title regardless of the stored label).
 */
export function RelatedActionsEditor({
  value,
  onChange,
}: {
  value: RelatedAction[];
  onChange: (v: RelatedAction[]) => void;
}) {
  const [samples, setSamples] = useState<{ id: string; title: string }[]>([]);
  const [docs, setDocs] = useState<{ doc_id: string; title: string }[]>([]);

  useEffect(() => {
    (async () => {
      const [{ data: s }, { data: d }] = await Promise.all([
        supabase.from("sample_documents" as any).select("id,title").order("title"),
        supabase
          .from("sop_documents" as any)
          .select("doc_id,title")
          .eq("status", "published")
          .order("doc_id"),
      ]);
      setSamples(((s as any[]) || []).map((r) => ({ id: r.id, title: r.title })));
      setDocs(((d as any[]) || []).map((r) => ({ doc_id: r.doc_id, title: r.title })));
    })();
  }, []);

  function update(i: number, patch: Partial<RelatedAction>) {
    const next = value.map((a, idx) => (idx === i ? { ...a, ...patch } : a));
    onChange(next);
  }

  return (
    <div className="space-y-2">
      {value.map((a, i) => (
        <div key={i} className="flex flex-col md:flex-row gap-2 items-start">
          <Select
            value={a.type}
            onValueChange={(v) => update(i, { type: v as RelatedAction["type"], key: "" })}
          >
            <SelectTrigger className="w-full md:w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="workflow">Workflow</SelectItem>
              <SelectItem value="sample">Sample</SelectItem>
              <SelectItem value="doc">Document</SelectItem>
            </SelectContent>
          </Select>

          {a.type === "workflow" ? (
            <Select value={a.key} onValueChange={(v) => update(i, { key: v })}>
              <SelectTrigger className="w-full md:w-64"><SelectValue placeholder="Pick a tool" /></SelectTrigger>
              <SelectContent>
                {Object.values(STEW_TOOL_REGISTRY).map((t) => (
                  <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : a.type === "sample" ? (
            <Select value={a.key} onValueChange={(v) => update(i, { key: v })}>
              <SelectTrigger className="w-full md:w-64"><SelectValue placeholder="Pick a sample" /></SelectTrigger>
              <SelectContent>
                {samples.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Select value={a.key} onValueChange={(v) => update(i, { key: v })}>
              <SelectTrigger className="w-full md:w-64"><SelectValue placeholder="Pick a document" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {docs.map((d) => (
                  <SelectItem key={d.doc_id} value={d.doc_id}>
                    {d.doc_id} — {d.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Input
            value={a.label || ""}
            onChange={(e) => update(i, { label: e.target.value })}
            placeholder="Button label (optional)"
            className="w-full md:flex-1"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange(value.filter((_, idx) => idx !== i))}
            aria-label="Remove action"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...value, { type: "doc", key: "", label: "" }])}
      >
        <Plus className="h-3.5 w-3.5 mr-1" /> Add action
      </Button>
    </div>
  );
}
